import { Avatar, Box, Card, Stack, Typography } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import type { KnownNotificationResource } from '../../../api/notifications'
import { IconTeams } from '../../../icons/IconTeams'
import { NotificationEntityHeader } from './NotificationEntityHeader'
import { mediaSourceUrl } from './utils'
import { createNotificationNavHandlers, getActorPathKey, getTeamSlug } from './notificationNav'
import { ActorNameSpan, ProjectNameQuoted } from './notificationTypography'
import { notificationBodyWrapSx } from './notificationTextClamp'

type Props = {
  notification: Extract<KnownNotificationResource, { type: 'team.join_request.canceled' }>
}

function teamTitleFromBlock(team: { title?: string; name?: string } | null | undefined): string {
  const title = typeof team?.title === 'string' ? team.title.trim() : ''
  if (title.length > 0) return title
  const name = typeof team?.name === 'string' ? team.name.trim() : ''
  return name.length > 0 ? name : 'команда'
}

export function TeamJoinRequestCanceledNotificationCard({ notification }: Props) {
  const navigate = useNavigate()
  const { onTeamClick, onUserClick } = createNotificationNavHandlers(navigate)
  const payload = notification.payload ?? {}
  const team = payload.team ?? {}
  const actor = payload.actor ?? {}

  const teamTitle = teamTitleFromBlock(team)
  const actorName = typeof actor.fullname === 'string' && actor.fullname.trim().length > 0 ? actor.fullname : 'Пользователь'
  const teamAvatarSrc = mediaSourceUrl(team.image_url) ?? undefined
  const teamSlug = getTeamSlug(team)
  const actorPathKey = getActorPathKey(actor, notification.actor_user_id)
  const hasActor = Boolean(payload?.actor)

  return (
    <Card variant="primaryAdaptiveCard" sx={{ p: 2 }}>
      <Stack direction="column" minWidth={0} flex={1} gap={1.25}>
        <Box
          onClick={(e) => onTeamClick(e, teamSlug)}
          sx={{ cursor: teamSlug ? 'pointer' : 'default', alignSelf: 'stretch' }}
        >
          <NotificationEntityHeader
            title={teamTitle}
            avatarSrc={teamAvatarSrc}
            fallbackIcon={<IconTeams fontSize="small" sx={{ color: 'inherit' }} />}
            subtitle="Заявка отменена"
          />
        </Box>

        <Stack direction="row" gap={1.5} alignItems="center" minWidth={0}>
          {hasActor && (
            <Box
              onClick={actorPathKey ? (e) => onUserClick(e, actorPathKey) : undefined}
              sx={{ cursor: actorPathKey ? 'pointer' : 'default', flexShrink: 0 }}
            >
              <Avatar src={mediaSourceUrl(actor.avatar_url) ?? undefined} alt={actorName} sx={{ width: 34, height: 34 }} />
            </Box>
          )}

          <Typography variant="black-14-normal" sx={notificationBodyWrapSx}>
            {hasActor ? (
              <>
                <ActorNameSpan pathKey={actorPathKey} onUserClick={onUserClick}>
                  {actorName}
                </ActorNameSpan>
                {' отменил(а) заявку на участие в '}
                <ProjectNameQuoted name={teamTitle} pathKey={teamSlug} onNameClick={onTeamClick} />
              </>
            ) : (
              <>
                Заявка на участие в <ProjectNameQuoted name={teamTitle} pathKey={teamSlug} onNameClick={onTeamClick} /> отменена
              </>
            )}
          </Typography>
        </Stack>

        <Typography variant="black-12-light" noWrap sx={{ opacity: 0.8 }}>
          {notification.read_at ? 'Прочитано' : 'Непрочитано'} • {new Date(notification.created_at).toLocaleString()}
        </Typography>
      </Stack>
    </Card>
  )
}
